import {
  Button,
  ButtonGroup,
  Calendar,
  HorizontalSelect,
  Link,
  Loading
} from 'components'
import { AllAlgoChallenges, AllFrontendChallenges } from 'features/AlgoEditor'
import { AllBackendChallengesDetails } from 'features/Backend'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react'
import { useLoginState } from 'state'

import { RocketIcon } from '@radix-ui/react-icons'
import { tx } from '@twind/core'

import { SQLChallengesDetails } from '../../SQLBrowser'

import QuestionRow from './QuestionRow'

const CompanyFilterDynamic = dynamic(() => import('./CompanyFilter'))

type questionsType =
  | AllAlgoChallenges[]
  | AllFrontendChallenges[]
  | AllBackendChallengesDetails[]
  | SQLChallengesDetails[]

export type ChallengesProps = {
  leaderboardType: 'DSA' | 'Frontend' | 'Backend' | 'Beta-Backend' | 'SQL'
  showTopics: boolean
  showCompany: boolean
  showLeaderboard?: boolean
  companies?: Array<{ id: string; name: string; image_url: string }>
  questions: questionsType
  difficultyOptions?: Array<{
    key: string
    label: string
    color: string
    background: string
  }>
  topics?: Array<{
    key: string
    label: string
  }>
  selectedDifficulties: string[]
  selectedTopics?: string[]
  selectedCompanies?: string[]
  onFeelingLucky: () => void
  setSelectedDifficulties: Dispatch<SetStateAction<string[]>>
  setSelectedTopics?: Dispatch<SetStateAction<string[]>>
  setSelectedCompanies?: Dispatch<SetStateAction<string[]>>
  tableRef?: React.RefObject<HTMLDivElement> | null
  isFetchingNextPage?: boolean
  isLoading?: boolean
}

const Challenges = (props: ChallengesProps) => {
  const {
    leaderboardType,
    showTopics,
    showCompany,
    showLeaderboard = true,
    companies,
    questions,
    difficultyOptions,
    topics,
    selectedDifficulties,
    selectedTopics,
    selectedCompanies,
    onFeelingLucky,
    setSelectedDifficulties,
    setSelectedTopics,
    setSelectedCompanies,
    tableRef = null,
    isFetchingNextPage = false,
    isLoading = false
  } = props
  const { user } = useLoginState()
  const router = useRouter()

  const handleQuestionURL = useCallback(
    (id: number) => {
      const question = questions[id]
      switch (leaderboardType) {
        case 'DSA':
          return `/algo-challenges/${question.slug}?tab=question`
        case 'Frontend':
          return `/frontend-challenges/${question.topic}/${question.slug}/${question.id}`
        case 'Backend':
          return `/backend-challenges/${question.slug}/${question.id}`
        case 'Beta-Backend':
          return `/beta-backend-challenges/${question.slug}/${question.id}`
        case 'SQL':
          return `/sql-challenges/${question.slug}`
        default:
          return ''
      }
    },
    [leaderboardType, questions]
  )

  const handleLeaderBoardURL = useCallback(() => {
    switch (leaderboardType) {
      case 'DSA':
        return router.push('/dsa-leaderboard')
      case 'Frontend':
        return router.push('/frontend-leaderboard')
      case 'Backend':
        return router.push('/backend-leaderboard')
      case 'SQL':
        return router.push('/sql-leaderboard')
      default:
        return ''
    }
  }, [leaderboardType, router])

  const ignoreShowOption = useMemo(() => {
    const ignore = ['Backend', 'Beta-Backend']
    return !ignore.includes(leaderboardType)
  }, [leaderboardType])

  return (
    <div className="flex-1 flex flex-row max-w-7xl mx-auto w-full my-8 px-6">
      <div className="max-w-full w-full flex flex-col md:mr-8 overflow-x-auto">
        <h1 className="sr-only">{`${leaderboardType}`} Challenges</h1>

        <div className="flex flex-wrap justify-center sm:justify-between items-center mt-3 md:mt-0 mb-3 overflow-x-auto">
          {difficultyOptions && (
            <div className="flex my-2 mr-2">
              <ButtonGroup
                options={difficultyOptions}
                selected={selectedDifficulties}
                onChangeValue={setSelectedDifficulties}
                itemClassName={(value) => value.color}
                multi
              />
            </div>
          )}

          <div className="my-2 p-px bg-gradient-to-br from-primary-dark to-pink-700 rounded-md">
            <Button
              className="bg-zinc-900 whitespace-nowrap hover:bg-transparent"
              variant="transparent"
              onClick={onFeelingLucky}>
              <RocketIcon className="w-4 h-4 mr-2" />
              <span>{"I'm feeling lucky"}</span>
            </Button>
          </div>
        </div>

        {showTopics && (
          <div className="mb-4 overflow-x-auto">
            <HorizontalSelect
              hideLabel
              label="Filter by topics"
              options={topics || []}
              selected={selectedTopics || []}
              onChangeValue={(val) =>
                setSelectedTopics && setSelectedTopics(val)
              }
            />
          </div>
        )}
        {/* Table */}
        {isLoading ? (
          <Loading centerParent />
        ) : (
          <div
            className="flex max-w-full overflow-auto max-h-[80vh]"
            ref={tableRef}>
            <div className="flex-1 inline-block">
              <table className="leading-normal w-full" cellPadding="10">
                <thead className="bg-zinc-800">
                  <tr className="text-left border-b-1 border-zinc-600">
                    <th className="text-center">#</th>
                    <th>Title</th>
                    {ignoreShowOption && <th>Topic</th>}
                    <th>Score</th>
                    <th>Difficulty</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>

                <tbody>
                  {questions &&
                    questions.map((question, index) => (
                      <QuestionRow
                        key={question.id}
                        challenge={question}
                        index={index}
                        questionURL={handleQuestionURL(index)}
                      />
                    ))}

                  {isFetchingNextPage && (
                    <tr className="flex justify-between bg-transparent rounded-xl">
                      <td />

                      <td className="flex justify-center ">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-100" />
                      </td>

                      <td />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="min-w-[300px] hidden md:flex flex-col items-stretch space-y-3">
        {showLeaderboard && ignoreShowOption && (
          <Button
            className="w-full border border-zinc-600"
            variant="transparent"
            onClick={handleLeaderBoardURL}>
            Checkout Leaderboard 🏆 &rarr;
          </Button>
        )}

        <div className="relative">
          <Calendar
            data={user?.activity}
            className={tx(
              'p-5 rounded-md border-1 border-zinc-600',
              !user && 'filter blur-sm pointer-events-none'
            )}
          />
          {!user && (
            <div className="px-5 font-bold w-full flex flex-col items-center text-center space-y-3 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <span>Please sign in to unlock Daily Streaks.</span>

              <Link
                href="pages/login"
                className="w-min whitespace-nowrap bg-white text-primary-dark font-bold px-3 py-1.5 rounded-md text-center">
                Sign In
              </Link>
            </div>
          )}
        </div>

        {showCompany && (
          <div className="mt-4 max-w-[330px] p-4 border-1 border-zinc-600 rounded-md">
            <div className="font-medium mb-3">Companies</div>
            {companies && companies.length > 0 && (
              <CompanyFilterDynamic
                className="font-medium cursor-pointer hover:border-zinc-400 px-4 py-1"
                companies={companies}
                currentlySelectedCompanies={selectedCompanies}
                onChangeValue={setSelectedCompanies}
                numberOfCompaniesToShowInitially={20}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Challenges
